// Fragment-shader chunk (GLSL ES 3.00) classifying a fragment of an SDF
// cross-section as lying on the contour edge, shared by every shader that
// draws an iso-contour on a plane view so all detect it alike. No #version
// and no main(): the including shader declares highp float and sampler3D
// precision before it and loads the uniforms through ContourEdgeUniforms.
//
// Uniforms:
// - sampler         the ROI's 3D SDF texture, normalised so 0.5 is the iso
// - viewToTextureUV view-NDC direction -> texture-UV direction (w = 0)
// - pixelSize       one line-thickness step in view NDC, per axis
// - imageZRange     texture-UV Z of the image's first and last slice centre
// - imageXYRange    texture-UV X/Y of the image's outermost pixel centres,
//                   as (xMin, yMin, xMax, yMax)

uniform sampler3D sampler;
uniform mat4 viewToTextureUV;
uniform vec2 pixelSize;
uniform vec2 imageZRange;
uniform vec4 imageXYRange;

const float DIAGONAL = inversesqrt(2.0);
const float SDF_THRESHOLD = 0.5;
// Tolerance for the image-range tests, which uniforms (float32) and the
// cross-section's UV reach by different arithmetic. Clip and classification
// share it, so a kept fragment never sees all its neighbours out of range.
const float Z_CLIP_EPSILON = 1.0 / 100000.0;

// Whether a texture-UV Z lies within the image's first..last slice
// centre range (inclusive, within Z_CLIP_EPSILON).
bool isInsideImageZ(float z) {
    return !(z + Z_CLIP_EPSILON < imageZRange.x ||
             z > imageZRange.y + Z_CLIP_EPSILON);
}

// Whether a texture-UV position lies within the image's outermost
// voxel-centre range on all three axes.
bool isInsideImage(vec3 samplePos) {
    return !(samplePos.x + Z_CLIP_EPSILON < imageXYRange.x ||
             samplePos.x > imageXYRange.z + Z_CLIP_EPSILON ||
             samplePos.y + Z_CLIP_EPSILON < imageXYRange.y ||
             samplePos.y > imageXYRange.w + Z_CLIP_EPSILON) &&
           isInsideImageZ(samplePos.z);
}

// Inside/outside classification of the neighbour (x, y) line
// thicknesses away from 'center' in view space. Out-of-image-range
// returns false so the inside-to-outside transition where the
// structure is cut by the image boundary produces a closing edge,
// on every axis. X/Y out of the texture but still inside the image
// returns centerInside: that texture edge is an arbitrary crop
// plane and must not draw an edge halo.
bool sampleNeighbor(vec3 center, float x, float y, bool centerInside) {
    vec2 viewDelta = vec2(x * pixelSize.x, y * pixelSize.y);
    vec3 uvDelta = (viewToTextureUV * vec4(viewDelta, 0.0, 0.0)).xyz;
    vec3 samplePos = center + uvDelta;

    if (!isInsideImage(samplePos)) {
        return false;
    }
    if (samplePos.x <= 0.0 || samplePos.x >= 1.0 ||
        samplePos.y <= 0.0 || samplePos.y >= 1.0) {
        return centerInside;
    }

    return texture(sampler, samplePos).r >= SDF_THRESHOLD;
}

// 8-neighbour edge detection: the fragment at 'center' is on the
// contour if any neighbour's classification differs from its own.
bool isContourEdge(vec3 center, bool centerInside) {
    return
        sampleNeighbor(center, -DIAGONAL, -DIAGONAL, centerInside) != centerInside ||
        sampleNeighbor(center,       0.0,      -1.0, centerInside) != centerInside ||
        sampleNeighbor(center,  DIAGONAL, -DIAGONAL, centerInside) != centerInside ||
        sampleNeighbor(center,      -1.0,       0.0, centerInside) != centerInside ||
        sampleNeighbor(center,       1.0,       0.0, centerInside) != centerInside ||
        sampleNeighbor(center, -DIAGONAL,  DIAGONAL, centerInside) != centerInside ||
        sampleNeighbor(center,       0.0,       1.0, centerInside) != centerInside ||
        sampleNeighbor(center,  DIAGONAL,  DIAGONAL, centerInside) != centerInside;
}
